// Centralized print-tags renderer.
// Uses the bundled `jsbarcode` package (same dependency as the in-app <Barcode />)
// so printed tags ALWAYS match the current app bundle. No external CDN scripts —
// published sites can never serve stale print code.

import JsBarcode from "jsbarcode";
import jsPDF from "jspdf";

export interface PrintTagInput {
  name: string;
  price: number | string;
  upc?: string | null;
  image?: string | null;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Always encode as CODE128 — preserves the EXACT digits scanned/saved,
// with no check-digit recalculation that could silently change a UPC.
// All modern POS / handheld scanners read CODE128.
const pickFormat = (raw: string): { fmt: string; val: string } => {
  return { fmt: "CODE128", val: raw };
};

// Render a barcode to an inline SVG string using the bundled jsbarcode.
const renderBarcodeSvg = (raw: string): string => {
  if (!raw) return "";
  const { fmt, val } = pickFormat(raw);
  try {
    // jsbarcode supports an offscreen SVG element via the DOM.
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(svg, val, {
      format: fmt,
      width: 2,
      height: 40,
      fontSize: 0,
      displayValue: false,
      margin: 0,
      background: "#fff",
      lineColor: "#000",
    });
    const w = svg.getAttribute("width");
    const h = svg.getAttribute("height");
    if (w && h) svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.removeAttribute("width");
    svg.removeAttribute("height");
    svg.setAttribute("preserveAspectRatio", "none");
    svg.setAttribute("style", "width:100%;height:100%;display:block;");
    return new XMLSerializer().serializeToString(svg);
  } catch {
    return "";
  }
};

export const printPriceTags = (tags: PrintTagInput[], title = "Price Tags") => {
  if (!tags.length) return;
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const tagHtml = tags
    .map((t) => {
      const upc = (t.upc || "").toString().trim();
      const barcodeSvg = upc ? `<div class="bcwrap">${renderBarcodeSvg(upc)}</div>` : "";
      const priceNum = Number(t.price) || 0;
      return `
        <div class="dymo-tag">
          <div class="row">
            <div class="right">
              <div class="price">$${priceNum.toFixed(2)}</div>
            </div>
            <div class="left">
              <div class="brand">Lynx to the Past</div>
              <div class="name">${escapeHtml(t.name)}</div>
              ${barcodeSvg}
            </div>
          </div>
        </div>
      `;
    })
    .join("");

  printWindow.document.write(`<!doctype html>
<html><head><title>${escapeHtml(title)}</title>
<style>
  @page { size: 2in 0.75in; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .dymo-tag {
    width: 2in; height: 0.75in;
    box-sizing: border-box;
    padding: 0.05in 0.07in;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
    color: #000;
  }
  .dymo-tag:last-child { page-break-after: auto; break-after: auto; }
  .row { display: flex; flex-direction: row-reverse; align-items: center; gap: 0.06in; height: 100%; width: 100%; }
  .right { flex-shrink: 0; width: 0.7in; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .price { font-size: 18px; font-weight: 800; line-height: 1; }
  .left { flex: 1; min-width: 0; height: 100%; display: flex; flex-direction: column; justify-content: space-between; padding: 0.02in 0; }
  .brand { font-size: 7px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; line-height: 1; text-align: right; width: 100%; }
  .name { font-size: 8px; font-weight: 700; line-height: 1.05; text-align: right; width: 100%; display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
  .bcwrap { width: 100%; height: 0.32in; display: block; overflow: hidden; background: #fff; }
  .bcwrap svg { width: 100%; height: 100%; display: block; }
</style>
</head><body>
${tagHtml}
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 100); });</script>
</body></html>`);
  printWindow.document.close();
};

// 4in x 4in label variant — same layout/content, scaled up for a 4x4 printer.
export const printPriceTags4x4 = (tags: PrintTagInput[], title = "Price Tags 4x4") => {
  if (!tags.length) return;
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const tagHtml = tags
    .map((t) => {
      const upc = (t.upc || "").toString().trim();
      const barcodeSvg = upc ? `<div class="bcwrap">${renderBarcodeSvg(upc)}</div><div class="upc">${escapeHtml(upc)}</div>` : "";
      const priceNum = Number(t.price) || 0;
      const img = (t.image || "").toString().trim();
      const imgHtml = img ? `<div class="imgwrap"><img src="${escapeHtml(img)}" crossorigin="anonymous" /></div>` : "";
      return `
        <div class="big-tag">
          <div class="brand">Lynx to the Past</div>
          <div class="name">${escapeHtml(t.name)}</div>
          ${imgHtml}
          <div class="price">$${priceNum.toFixed(2)}</div>
          ${barcodeSvg}
        </div>
      `;
    })
    .join("");

  printWindow.document.write(`<!doctype html>
<html><head><title>${escapeHtml(title)}</title>
<style>
  @page { size: 4in 4in; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .big-tag {
    width: 4in; height: 4in;
    box-sizing: border-box;
    padding: 0.2in;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
    overflow: hidden;
    color: #000;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    text-align: center;
    gap: 0.06in;
  }
  .big-tag:last-child { page-break-after: auto; break-after: auto; }
  .brand { font-size: 18px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; line-height: 1; width: 100%; }
  .name { font-size: 18px; font-weight: 700; line-height: 1.15; width: 100%; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .imgwrap { width: 1.4in; height: 1.4in; display: flex; align-items: center; justify-content: center; background: #fff; }
  .imgwrap img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
  .price { font-size: 56px; font-weight: 900; line-height: 1; }
  .bcwrap { width: 100%; height: 0.8in; display: block; overflow: hidden; background: #fff; }
  .bcwrap svg { width: 100%; height: 100%; display: block; }
  .upc { width: 100%; text-align: center; font-size: 14px; font-weight: 700; letter-spacing: 0.12em; line-height: 1; margin-top: 0.03in; }
</style>
</head><body>
${tagHtml}
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 100); });</script>
</body></html>`);
  printWindow.document.close();
};

// Render a UPC barcode to a PNG data URL using an offscreen canvas.
const renderBarcodePng = (raw: string): string => {
  if (!raw) return "";
  const { fmt, val } = pickFormat(raw);
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, val, {
      format: fmt,
      width: 2,
      height: 60,
      fontSize: 0,
      displayValue: false,
      margin: 0,
      background: "#ffffff",
      lineColor: "#000000",
    });
    return canvas.toDataURL("image/png");
  } catch {
    return "";
  }
};

// Export tags as a multi-page PDF — one tag per 2in x 0.75in page,
// matching the on-screen / print layout exactly. Scannable barcodes.
export const exportPriceTagsPdf = (tags: PrintTagInput[], filename = "price-tags.pdf") => {
  if (!tags.length) return;

  // Page = single Dymo tag (2in x 0.75in)
  const pdf = new jsPDF({
    unit: "in",
    format: [2, 0.75],
    orientation: "landscape",
  });

  const pageW = 2;
  const pageH = 0.75;
  const padX = 0.07;
  const padY = 0.05;

  tags.forEach((t, i) => {
    if (i > 0) pdf.addPage([2, 0.75], "landscape");

    const upc = (t.upc || "").toString().trim();
    const priceNum = Number(t.price) || 0;

    // RIGHT column (price) — 0.7in wide, right-aligned block
    const priceColW = 0.7;
    const priceColX = pageW - padX - priceColW;
    pdf.setFont("courier", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`$${priceNum.toFixed(2)}`, priceColX + priceColW / 2, pageH / 2 + 0.06, {
      align: "center",
    });

    // LEFT column (brand / name / barcode) — fills remaining space, right-aligned text
    const leftX = padX;
    const leftW = pageW - padX * 2 - priceColW - 0.06; // gap 0.06in
    const rightEdge = leftX + leftW;

    // Brand
    pdf.setFont("courier", "bold");
    pdf.setFontSize(6);
    pdf.text("LYNX TO THE PAST", rightEdge, padY + 0.07, { align: "right" });

    // Item name (truncate to fit one line)
    pdf.setFont("courier", "bold");
    pdf.setFontSize(7);
    const nameLines = pdf.splitTextToSize(t.name, leftW);
    const firstLine = (nameLines[0] || "").toString();
    pdf.text(firstLine, rightEdge, padY + 0.18, { align: "right" });

    // Barcode (PNG) — bottom of left column, full width
    if (upc) {
      const dataUrl = renderBarcodePng(upc);
      if (dataUrl) {
        const bcH = 0.3;
        const bcY = pageH - padY - bcH;
        pdf.addImage(dataUrl, "PNG", leftX, bcY, leftW, bcH, undefined, "FAST");
      }
    }
  });

  pdf.save(filename);
};
